import { tv, type VariantProps } from "tailwind-variants"
import { textColorVariants } from "../../styles/utils/color"
import { classNames } from "../../utils/classNames"
import { SkeletonLine, type SkeletonProps } from "../Skeleton"
import { Slot } from "../Slot"
import type { TextBodyProps } from "./TextBody"
import { textBodySkeletonVariants, textBodyVariants } from "./TextBody"

export const textLabelVariants = tv({
  extend: textBodyVariants,
  base: "font-mono uppercase",
  variants: {
    size: {
      "2xs": "text-2xs",
    },
  },
})

export type TextLabelProps = Omit<TextBodyProps, "size"> &
  VariantProps<typeof textLabelVariants>

/**
 * This can be used for things that are not explicitly form labels, but more so just for information purposes.
 */
function TextLabel({
  asChild,
  className,
  weight,
  color,
  size = "xs",
  ...rest
}: TextLabelProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      className={classNames(
        "leading-tight",
        textLabelVariants({
          weight,
          size,
        }),
        textColorVariants({ color }),
        className,
      )}
      {...rest}
    />
  )
}

const textLabelSkeletonVariants = tv({
  extend: textBodySkeletonVariants,
  variants: {
    size: {
      "2xs": "my-[2.5px] h-2.5",
    },
  },
})

const TextLabelSkeleton = ({
  className,
  size = "xs",
  ...rest
}: SkeletonProps & Pick<TextLabelProps, "size" | "className">) => {
  return (
    <SkeletonLine
      className={classNames(textLabelSkeletonVariants({ size }), className)}
      {...rest}
    />
  )
}

export { TextLabel, TextLabelSkeleton }
